import { useState, useEffect } from "react";
import "../styles/SpotifyNowPlaying.css";

function formatTime(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function SpotifyNowPlaying() {
  const [track, setTrack] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Poll the function every 15s (skipped while the tab is hidden)
  useEffect(() => {
    const load = () => {
      if (document.visibilityState === "hidden") return;
      fetch("/.netlify/functions/spotify-now-playing")
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => {
          setTrack(data);
          setFetchedAt(Date.now());
          setFailed(false);
        })
        .catch((err) => {
          console.error("Spotify status failed:", err);
          setFailed(true);
        })
        .finally(() => setLoading(false));
    };

    load();
    const id = setInterval(load, 15000);
    document.addEventListener("visibilitychange", load);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", load);
    };
  }, []);

  // Tick every second so the progress bar moves between polls
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return <div className="spotify-card spotify-card--idle">Loading Spotify...</div>;
  }
  if (failed && !track) return null;

  if (!track || !track.title) {
    return (
      <div className="spotify-card spotify-card--idle">
        <span className="spotify-card__label">Spotify</span>
        <p className="spotify-card__idle-text">Not listening to anything right now</p>
      </div>
    );
  }

  const elapsed = track.isPlaying ? now - fetchedAt : 0;
  const progress = Math.min(track.progressMs + elapsed, track.durationMs);
  const pct = track.durationMs ? (progress / track.durationMs) * 100 : 0;

  return (
    <a
      className={`spotify-card spotify-card--${track.isPlaying ? "playing" : "paused"}`}
      href={track.url || "https://open.spotify.com"}
      target="_blank"
      rel="noopener noreferrer"
    >
      {track.albumArt && <img className="spotify-card__art" src={track.albumArt} alt="" />}
      <div className="spotify-card__body">
        <span className="spotify-card__label">
          {track.isPlaying && (
            <span className="spotify-card__bars">
              <i />
              <i />
              <i />
            </span>
          )}
          {track.isPlaying ? "Now playing" : "Paused"}
        </span>
        <strong className="spotify-card__title">{track.title}</strong>
        <span className="spotify-card__artist">{track.artist}</span>
        <div className="spotify-card__progress">
          <div className="spotify-card__progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="spotify-card__times">
          <span>{formatTime(progress)}</span>
          <span>{formatTime(track.durationMs)}</span>
        </div>
      </div>
    </a>
  );
}

export default SpotifyNowPlaying;